// src/pages/admin/users/Reviews.jsx
import { useEffect, useMemo, useState } from "react";
import {
  Modal,
  Typography,
  Button,
  Tag,
  Rate,
  Switch,
  Space,
  message,
} from "antd";
import axios from "../../../requests/axios";
import routes from "../../../requests/routes";
import classes from "./AdminReviews.module.scss";

import TableWithExport from "../../../generic components/TableWithExport/TableWithExport";
import {
  fmtDate,
  getCreated,
  getEmail,
  getName,
  getPhone,
} from "../../../generic components/TableWithExport/TableWithExport.utils";
import TextArea from "antd/es/input/TextArea";
const { Text } = Typography;

const AdminReviews = () => {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const [current, setCurrent] = useState(null);

  const fetchReviews = async () => {
    try {
      setLoading(true);
      const res = await axios.get(routes.getAdminReviews);
      const list = res?.data?.reviews || res?.data || [];
      setData(Array.isArray(list) ? list : []);
    } catch (e) {
      console.error("Users fetch error:", e?.message);
      setData([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchReviews();
  }, [open]);

  const columns = [
    { title: "#", width: 64, align: "center", render: (_v, _r, i) => i + 1 },
    {
      title: "Name",
      sorter: (a, b) => getName(a).localeCompare(getName(b)),
      render: (_, r) => <Text strong>{getName(r) || "—"}</Text>,
    },
    {
      title: "Email",
      sorter: (a, b) => getEmail(a).localeCompare(getEmail(b)),
      render: (_, r) => <Text copyable>{getEmail(r) || "—"}</Text>,
    },
    {
      title: "Phone",
      responsive: ["md"],
      render: (_, r) => getPhone(r) || "—",
    },

    {
      title: "Program",
      responsive: ["md"],
      render: (_, r) => r.program?.name,
    },
    {
      title: "Published",
      width: 150,
      render: (_, r) => (
        <Space>
          <Switch
            checked={!!r.isPublished}
            onChange={() => updateReview(r, { isPublished: !r.isPublished })}
          />
          {r.isPublished ? (
            <Tag color="green">Published</Tag>
          ) : (
            <Tag>Pending</Tag>
          )}
        </Space>
      ),
    },
    {
      title: "Homepage",
      render: (_, r) =>
        r?.isHomepage ? (
          <Tag color="green">Yes</Tag>
        ) : (
          <Tag color="red">No</Tag>
        ),
    },
    {
      title: "Featured",
      render: (_, r) =>
        r?.isFeatured ? (
          <Tag color="green">Yes</Tag>
        ) : (
          <Tag color="red">No</Tag>
        ),
    },
    {
      title: "Created",
      sorter: (a, b) =>
        new Date(getCreated(a) || 0) - new Date(getCreated(b) || 0),
      render: (_, r) => fmtDate(getCreated(r)),
    },
    {
      title: "Actions",
      fixed: "right",
      width: 120,
      render: (_, record) => (
        <Space>
          <Button
            danger
            type="link"
            onClick={() => {
              Modal.confirm({
                title: "Delete review",
                content:
                  "Are you sure you want to delete this review? This action cannot be undone.",
                okText: "Delete",
                okType: "danger",
                cancelText: "Cancel",
                onOk: async () => {
                  try {
                    await axios.delete(routes.deleteReview(record._id));
                    message.success("Review deleted");
                    fetchReviews();
                  } catch (e) {
                    message.error("Failed to delete review");
                  }
                },
              });
            }}
          >
            Delete
          </Button>
        </Space>
      ),
    },
  ];

  const updateReview = async (review, tags) => {
    await axios
      .post(routes.reviewApprove, {
        reviewId: review._id,
        isPublished: tags.isPublished ?? review.isPublished,
        isHomepage: tags.isHomepage ?? review.isHomepage,
        isFeatured: tags.isFeatured ?? review.isFeatured,
      })
      .then((res) => {
        fetchReviews();
      });
  };

  return (
    <div className={classes.wrapper}>
      <h2 className="adminTitle">Reviews</h2>

      <TableWithExport
        setCurrent={setCurrent}
        setOpen={setOpen}
        data={data}
        loading={loading}
        columns={columns}
        tableName={"reviews"}
        filters={false}
      />

      <Modal
        open={open}
        title={getName(current) || "Review details"}
        onCancel={() => setOpen(false)}
        onOk={() => setOpen(false)}
        width={720}
        footer={[
          <Button key="submit" type="primary" onClick={() => setOpen(false)}>
            Done
          </Button>,
        ]}
      >
        <div className={classes.review}>
          <TextArea id="review" value={current?.text} />
          <Typography>
            <span style={{ fontWeight: "700" }}>Program: </span>
            <Text copyable>{current?.program.name}</Text>
          </Typography>
          <Typography>
            <span style={{ fontWeight: "700" }}>Phone: </span>
            <Text copyable>{current?.user.phoneNO}</Text>
          </Typography>
          <Rate disabled value={current?.rating || 0} />
          <div className={classes.tags}>
            {current?.isPublished === null ? (
              <Tag color="gray">Pending</Tag>
            ) : current?.isPublished ? (
              <Tag color="green">Puplished</Tag>
            ) : (
              <Tag color="red">Unpublished</Tag>
            )}
            {current?.isHomepage ? (
              <Tag color="green">In Homepage</Tag>
            ) : (
              <Tag color="red">Not in Homepage</Tag>
            )}
            {current?.isFeatured ? (
              <Tag color="green">Featured</Tag>
            ) : (
              <Tag color="red">Not Featured</Tag>
            )}
          </div>
          <div className={classes.review__actions}>
            <div className={classes.review__actions__btn}>
              <span>Publish?:</span>
              <Button
                disabled={
                  current?.isPublished !== null && !current?.isPublished
                }
                type="link"
                onClick={() => updateReview(current, { isPublished: false })}
              >
                {!current?.isPublished ? "Declined" : "Decline"}
              </Button>
              /
              <Button
                disabled={current?.isPublished}
                type="link"
                onClick={() => updateReview(current, { isPublished: true })}
              >
                {current?.isPublished ? "Approved" : "Approve"}
              </Button>
            </div>
            <div className={classes.review__actions__btn}>
              <span>Homepage?:</span>
              <Button
                disabled={!current?.isHomepage}
                type="link"
                onClick={() => updateReview(current, { isHomepage: false })}
              >
                {!current?.isHomepage ? "Declined" : "Decline"}
              </Button>
              /
              <Button
                disabled={current?.isHomepage}
                type="link"
                onClick={() => updateReview(current, { isHomepage: true })}
              >
                {current?.isHomepage ? "Approved" : "Approve"}
              </Button>
            </div>
            <div className={classes.review__actions__btn}>
              <span>Featured?:</span>
              <Button
                disabled={!current?.isFeatured}
                type="link"
                onClick={() => updateReview(current, { isFeatured: false })}
              >
                {!current?.isFeatured ? "Declined" : "Decline"}
              </Button>
              /
              <Button
                disabled={current?.isFeatured}
                type="link"
                onClick={() => updateReview(current, { isFeatured: true })}
              >
                {current?.isFeatured ? "Approved" : "Approve"}
              </Button>
            </div>
          </div>
        </div>
      </Modal>
    </div>
  );
};

export default AdminReviews;
